export interface Notification {
  id: number;
  user_id: number;
  type: string;
  title: string;
  message: string;
  category: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  related_entity_type?: string;
  related_entity_id?: number;
  action_url?: string;
  action_label?: string;
  is_read: boolean;
  email_sent: boolean;
  email_sent_at?: string;
  read_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationQuery {
  page?: number;
  limit?: number;
  filter?: 'all' | 'read' | 'unread';
  category?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export interface NotificationResponse {
  notifications: Notification[];
  pagination: {
    total: number;
    pages: number;
    current: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  unreadCount: number;
}

export interface NotificationCount {
  total: number;
  unread: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface NotificationPreferences {
  categories: Record<string, {
    emailEnabled: boolean;
    inAppEnabled: boolean;
  }>;
}

export interface BulkActionRequest {
  notificationIds: number[];
  action: 'mark_read' | 'mark_unread' | 'delete';
}

export interface UpdatePreferencesRequest {
  preferences: Record<string, {
    emailEnabled: boolean;
    inAppEnabled: boolean;
  }>;
}

// Notification categories
export type NotificationCategory = 
  | 'schedule_created'
  | 'schedule_modified'
  | 'change_request_response'
  | 'shift_swap'
  | 'task_assigned'
  | 'system'
  | 'reminder';

// Notification types by category
export type ScheduleCreatedType = 'shift_assigned' | 'recurring_shift_assigned';
export type ScheduleModifiedType = 'shift_time_changed' | 'shift_date_changed' | 'shift_type_changed' | 'shift_cancelled';
export type ChangeRequestResponseType = 'request_approved' | 'request_rejected' | 'request_under_review' | 'swap_request_received' | 'swap_request_accepted' | 'swap_request_declined';
export type ShiftSwapType = 'swap_proposal' | 'swap_accepted' | 'swap_declined' | 'swap_approved';
export type TaskAssignedType = 'task_assigned';
export type SystemType = 'maintenance' | 'announcement' | 'policy_update';
export type ReminderType = 'shift_reminder_24h' | 'shift_reminder_2h' | 'shift_starting_soon';

// Priority levels
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

// Filter options
export type NotificationFilter = 'all' | 'read' | 'unread';

// Category display names
export const CATEGORY_DISPLAY_NAMES: Record<NotificationCategory, string> = {
  schedule_created: 'Schedule Created',
  schedule_modified: 'Schedule Modified',
  change_request_response: 'Change Request Response',
  shift_swap: 'Shift Swap',
  task_assigned: 'Task Assigned',
  system: 'System',
  reminder: 'Reminder'
};

// Priority display names
export const PRIORITY_DISPLAY_NAMES: Record<NotificationPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent'
};

// Priority colors
export const PRIORITY_COLORS: Record<NotificationPriority, string> = {
  low: '#6b7280',
  normal: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444'
};

// Category icons (using Material-UI icon names)
export const CATEGORY_ICONS: Record<NotificationCategory, string> = {
  schedule_created: 'Schedule',
  schedule_modified: 'Edit',
  change_request_response: 'SwapHoriz',
  shift_swap: 'SwapHoriz',
  task_assigned: 'Assignment',
  system: 'Info',
  reminder: 'Alarm'
}; 